import React, { useEffect, useMemo, useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import { getAvailability, createBooking, trackEvent } from '../api';
import { ArrowLeft, Clock, MessageCircle, Check, Calendar as CalIcon, Loader2, MapPin } from 'lucide-react';

function brl(n) { return `R$ ${Number(n || 0).toFixed(2).replace('.', ',')}`; }
function fmtDateLabel(d) {
  const names = ['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb'];
  return `${names[d.getDay()]} ${String(d.getDate()).padStart(2, '0')}/${String(d.getMonth() + 1).padStart(2, '0')}`;
}
function toYMD(d) { return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`; }

export default function BookingFlow({ open, onClose, biosite }) {
  const [step, setStep] = useState(1);
  const [service, setService] = useState(null);
  const [dateObj, setDateObj] = useState(null);
  const [slot, setSlot] = useState('');
  const [slots, setSlots] = useState([]);
  const [loadingSlots, setLoadingSlots] = useState(false);
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [note, setNote] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [confirmed, setConfirmed] = useState(null);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!open) {
      setTimeout(() => {
        setStep(1); setService(null); setDateObj(null); setSlot(''); setSlots([]);
        setName(''); setPhone(''); setNote(''); setConfirmed(null); setError('');
      }, 200);
    }
  }, [open]);

  const sch = biosite?.scheduling || {};
  const services = (sch.services || []).filter((s) => s.enabled !== false);

  const next14 = useMemo(() => {
    const arr = []; const base = new Date(); base.setHours(0, 0, 0, 0);
    const maxDays = sch.maxDaysAhead || 30;
    for (let i = 0; i < Math.min(maxDays, 21); i++) { const d = new Date(base); d.setDate(base.getDate() + i); arr.push(d); }
    return arr;
  }, [sch.maxDaysAhead]);

  const loadSlots = async (svc, d) => {
    setLoadingSlots(true); setSlot('');
    try {
      const data = await getAvailability(biosite.slug, toYMD(d), svc.id);
      setSlots(data.slots || []);
    } catch { setSlots([]); }
    finally { setLoadingSlots(false); }
  };

  const submit = async () => {
    if (!name.trim() || !phone.trim()) { setError('Preencha nome e telefone.'); return; }
    setSubmitting(true); setError('');
    try {
      const bk = await createBooking(biosite.slug, {
        serviceId: service.id, date: toYMD(dateObj), time: slot,
        customerName: name, customerPhone: phone, note,
      });
      setConfirmed(bk);
      trackEvent(biosite.slug, 'click', 'booking');
      setStep(5);
    } catch (e) {
      setError(e.response?.data?.detail || 'Erro ao agendar. Tente outro horário.');
    } finally { setSubmitting(false); }
  };

  const whatsAppMsg = () => {
    if (!confirmed) return '';
    const addr = sch.address ? `\nEndereço: ${sch.address}` : '';
    const lines = [
      `Olá, ${biosite.name}!`,
      `Confirmando meu agendamento:`,
      `• Serviço: ${confirmed.serviceName}`,
      `• Data: ${confirmed.date} às ${confirmed.time}`,
      `• Cliente: ${confirmed.customerName}`,
      `• Telefone: ${confirmed.customerPhone}${addr}`,
    ];
    return lines.join('\n');
  };
  const whatsAppUrl = () => {
    const to = (sch.confirmationWhatsapp || '').replace(/\D/g, '');
    const base = to ? `https://wa.me/${to}` : 'https://wa.me/';
    return `${base}?text=${encodeURIComponent(whatsAppMsg())}`;
  };
  const calendarUrl = () => {
    if (!confirmed) return '';
    const start = new Date(`${confirmed.date}T${confirmed.time}:00`);
    const end = new Date(start.getTime() + (confirmed.durationMin || 30) * 60000);
    const fmt = (d) => d.toISOString().replace(/[-:]/g, '').replace(/\.\d{3}Z$/, 'Z');
    const text = encodeURIComponent(`${confirmed.serviceName} - ${biosite.name}`);
    const details = encodeURIComponent(`Agendamento com ${biosite.name}\nCliente: ${confirmed.customerName}`);
    const location = encodeURIComponent(sch.address || '');
    return `https://calendar.google.com/calendar/render?action=TEMPLATE&text=${text}&dates=${fmt(start)}/${fmt(end)}&details=${details}&location=${location}`;
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-md bg-white max-h-[90vh] overflow-y-auto">
        {/* Step 1: service */}
        {step === 1 && (
          <>
            <DialogHeader>
              <DialogTitle className="font-display">Agendar Horário</DialogTitle>
              <DialogDescription>Escolha o serviço</DialogDescription>
            </DialogHeader>
            <div className="space-y-2">
              {services.length === 0 && <div className="text-sm text-neutral-500 text-center py-8">Nenhum serviço cadastrado ainda.</div>}
              {services.map((s) => (
                <button key={s.id} onClick={() => { setService(s); setStep(2); }}
                  className="w-full text-left bg-neutral-50 hover:bg-neutral-100 border border-neutral-200 rounded-xl p-3 flex items-center gap-3 transition">
                  <div className="w-14 h-14 rounded-lg bg-neutral-200 overflow-hidden flex-shrink-0">
                    {s.image ? <img src={s.image} alt="" className="w-full h-full object-cover" /> : <div className="w-full h-full grid place-items-center text-neutral-400"><CalIcon size={18} /></div>}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="font-semibold text-neutral-900">{s.name}</div>
                    {s.description && <div className="text-xs text-neutral-500 line-clamp-2">{s.description}</div>}
                    <div className="text-xs mt-1 flex gap-2 text-neutral-600">
                      <span className="inline-flex items-center gap-1"><Clock size={11} /> {s.durationMin || 30} min</span>
                      <span>• {brl(s.price)}</span>
                    </div>
                  </div>
                </button>
              ))}
            </div>
          </>
        )}

        {/* Step 2: date */}
        {step === 2 && service && (
          <>
            <DialogHeader>
              <div className="flex items-center gap-2"><button onClick={() => setStep(1)}><ArrowLeft size={16} /></button>
                <DialogTitle className="font-display">Escolha o dia</DialogTitle></div>
              <DialogDescription>{service.name} • {service.durationMin || 30} min</DialogDescription>
            </DialogHeader>
            <div className="grid grid-cols-4 gap-2">
              {next14.map((d) => (
                <button key={d.toISOString()} onClick={() => { setDateObj(d); loadSlots(service, d); setStep(3); }}
                  className="bg-neutral-50 hover:bg-neutral-900 hover:text-white border border-neutral-200 rounded-xl p-2 text-center transition">
                  <div className="text-[10px] uppercase">{fmtDateLabel(d).split(' ')[0]}</div>
                  <div className="text-base font-bold">{String(d.getDate()).padStart(2, '0')}</div>
                  <div className="text-[10px] opacity-70">{String(d.getMonth() + 1).padStart(2, '0')}</div>
                </button>
              ))}
            </div>
          </>
        )}

        {/* Step 3: time */}
        {step === 3 && service && dateObj && (
          <>
            <DialogHeader>
              <div className="flex items-center gap-2"><button onClick={() => setStep(2)}><ArrowLeft size={16} /></button>
                <DialogTitle className="font-display">Escolha o horário</DialogTitle></div>
              <DialogDescription>{service.name} • {fmtDateLabel(dateObj)}</DialogDescription>
            </DialogHeader>
            {loadingSlots ? (
              <div className="grid place-items-center py-12"><Loader2 className="animate-spin text-neutral-400" /></div>
            ) : slots.length === 0 ? (
              <div className="text-center text-neutral-500 py-10 text-sm">Nenhum horário disponível neste dia.</div>
            ) : (
              <div className="grid grid-cols-3 gap-2">
                {slots.map((t) => (
                  <button key={t} onClick={() => { setSlot(t); setStep(4); }}
                    className="bg-neutral-50 hover:bg-neutral-900 hover:text-white border border-neutral-200 rounded-lg py-2.5 font-semibold text-sm transition">
                    {t}
                  </button>
                ))}
              </div>
            )}
          </>
        )}

        {/* Step 4: customer */}
        {step === 4 && service && dateObj && slot && (
          <>
            <DialogHeader>
              <div className="flex items-center gap-2"><button onClick={() => setStep(3)}><ArrowLeft size={16} /></button>
                <DialogTitle className="font-display">Seus dados</DialogTitle></div>
              <DialogDescription>{service.name} • {fmtDateLabel(dateObj)} às {slot}</DialogDescription>
            </DialogHeader>
            <div className="space-y-3">
              <div>
                <Label>Nome completo</Label>
                <Input value={name} onChange={(e) => setName(e.target.value)} className="mt-1" />
              </div>
              <div>
                <Label>Telefone / WhatsApp</Label>
                <Input value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="(11) 99999-9999" className="mt-1" />
              </div>
              <div>
                <Label>Observação (opcional)</Label>
                <Textarea value={note} onChange={(e) => setNote(e.target.value)} rows={2} className="mt-1" />
              </div>
              <div className="bg-neutral-50 border border-neutral-200 rounded-xl p-3 text-sm space-y-1">
                <div className="flex justify-between"><span className="text-neutral-500">Serviço</span><span className="font-semibold">{service.name}</span></div>
                <div className="flex justify-between"><span className="text-neutral-500">Data</span><span className="font-semibold">{fmtDateLabel(dateObj)} às {slot}</span></div>
                <div className="flex justify-between"><span className="text-neutral-500">Duração</span><span className="font-semibold">{service.durationMin || 30} min</span></div>
                <div className="flex justify-between"><span className="text-neutral-500">Valor</span><span className="font-semibold">{brl(service.price)}</span></div>
              </div>
              {error && <div className="text-sm text-red-600">{error}</div>}
              <Button onClick={submit} disabled={submitting} className="w-full gap-2 bg-emerald-600 hover:bg-emerald-700 text-white">
                {submitting ? <Loader2 className="animate-spin" size={14} /> : <Check size={14} />} Confirmar agendamento
              </Button>
            </div>
          </>
        )}

        {/* Step 5: success */}
        {step === 5 && confirmed && (
          <>
            <DialogHeader>
              <div className="flex flex-col items-center text-center">
                <div className="w-14 h-14 rounded-full bg-emerald-100 text-emerald-700 grid place-items-center mb-2"><Check size={28} /></div>
                <DialogTitle className="font-display">Agendamento confirmado!</DialogTitle>
                <DialogDescription>Guarde os detalhes abaixo.</DialogDescription>
              </div>
            </DialogHeader>
            <div className="bg-neutral-50 border border-neutral-200 rounded-xl p-3 text-sm space-y-1">
              <div className="flex justify-between"><span className="text-neutral-500">Serviço</span><span className="font-semibold">{confirmed.serviceName}</span></div>
              <div className="flex justify-between"><span className="text-neutral-500">Data</span><span className="font-semibold">{confirmed.date} às {confirmed.time}</span></div>
              <div className="flex justify-between"><span className="text-neutral-500">Cliente</span><span className="font-semibold">{confirmed.customerName}</span></div>
              <div className="flex justify-between"><span className="text-neutral-500">Telefone</span><span className="font-semibold">{confirmed.customerPhone}</span></div>
              {sch.address && <div className="flex items-start justify-between"><span className="text-neutral-500">Endereço</span><span className="font-semibold text-right max-w-[60%]">{sch.address}</span></div>}
            </div>
            <div className="grid grid-cols-1 gap-2 mt-2">
              <a href={whatsAppUrl()} target="_blank" rel="noopener noreferrer" className="w-full inline-flex items-center justify-center gap-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded-md h-10 font-semibold text-sm">
                <MessageCircle size={14} /> Enviar confirmação pelo WhatsApp
              </a>
              <a href={calendarUrl()} target="_blank" rel="noopener noreferrer" className="w-full inline-flex items-center justify-center gap-2 bg-neutral-900 hover:bg-neutral-800 text-white rounded-md h-10 font-semibold text-sm">
                <CalIcon size={14} /> Adicionar ao Google Calendar
              </a>
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
